import type { CvtConcernOption } from '../cvt-primary-concerns-modal.types';

import { colors } from '@packages/core/styles';
import { Icon, Typography } from '@packages/ui/shared';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
    activeOption: {},
    option: {
        alignItems: 'center',
        cursor: 'pointer',
        display: 'flex',
        gap: 8,
        padding: '8px 16px',
        '&$activeOption': {
            backgroundColor: colors.blueFour,
        },
    },
});

export type CvtConcernsMultiSelectOptionProps = {
    /** The row's unique id, which the combobox uses to tell screen readers when this row is active. */
    id: string;

    /** Whether this row is the active one, picked with the arrow keys or the mouse and toggled with Enter or Space. */
    isActive: boolean;

    /** Whether the option is selected, which checks its checkbox. */
    isSelected: boolean;

    /** Called with the option when the mouse moves over the row. */
    onHover: (option: CvtConcernOption) => void;

    /** Checks the option, or unchecks it when it is selected. */
    onToggle: (option: CvtConcernOption) => void;

    /** The option this row shows. */
    option: CvtConcernOption;
};

/**
 * An option in the concerns multi-select's menu.
 *
 * Renders a checkbox, checked while the option is selected, and the option's title.
 */
export function CvtConcernsMultiSelectOption({
    id,
    isActive,
    isSelected,
    onHover,
    onToggle,
    option,
}: CvtConcernsMultiSelectOptionProps) {
    const classes = useStyles();

    const iconColor = isSelected ? 'blueOne' : 'grayFive';
    const iconName = isSelected ? 'checkbox-marked' : 'checkbox-unmarked';

    function handleOptionClick() {
        onToggle(option);
    }

    function handleOptionMouseMove() {
        onHover(option);
    }

    return (
        <div
            aria-selected={isSelected}
            className={classNames(classes.option, { [classes.activeOption]: isActive })}
            id={id}
            onClick={handleOptionClick}
            onMouseMove={handleOptionMouseMove}
            role="option"
        >
            <Icon color={iconColor} name={iconName} />
            <Typography>{option.title}</Typography>
        </div>
    );
}
